<template>
  <div class="mobile-order-list">
    <!-- 页面头部 -->
    <div class="page-header">
      <h1>{{ $t('wholesaler.OrderList.line5_text') }}</h1>
    </div>

    <!-- 搜索栏 -->
    <div class="search-bar">
      <el-input
          v-model="searchKeyword"
          :placeholder="$t('wholesaler.OrderList.line12_placeholder')"
          prefix-icon="el-icon-search"
          clearable
          @input="handleSearch"
      />
    </div>

    <!-- 状态筛选 -->
    <div class="status-filter">
      <div
          v-for="item in statusOptions"
          :key="item.value"
          :class="['filter-item', { active: filterStatus === item.value }]"
          @click="setFilterStatus(item.value)"
      >
        {{ item.label }}
      </div>
    </div>

    <!-- 批量操作栏 -->
    <div class="batch-action-bar" v-if="selectedOrders.length > 0">
      <div class="batch-info">
        <span>{{ $t('wholesaler.OrderList.selectedOrders') }} {{ selectedOrders.length }} {{ $t('wholesaler.OrderList.orderUnit') }}</span>
        <el-button type="text" size="small" @click="clearSelection">{{ $t('wholesaler.OrderList.line35_text') }}</el-button>
      </div>
      <div class="batch-buttons">
        <el-button type="warning" size="small" @click="handleBatchPack" v-if="canBatchPack">
          {{ $t('wholesaler.OrderList.batchPack') }}
        </el-button>
        <el-button type="primary" size="small" @click="handleBatchAssign">
          {{ $t('wholesaler.OrderList.batchAssignDriver') }}
        </el-button>
      </div>
    </div>

    <!-- 订单列表 -->
    <div class="order-list">
      <div v-if="loading" class="loading-state">
        <div class="loading-icon">⏳</div>
        <p>{{ $t('common.loading') }}</p>
      </div>

      <div v-else-if="orders.length === 0" class="empty-state">
        <div class="empty-icon">📋</div>
        <p>{{ $t('common.noOrder') }}</p>
      </div>

      <div v-else>
        <!-- 全选 checkbox（仅在已确认状态下显示） -->
        <div class="select-all-bar" v-if="filterStatus === 'CONFIRMED'">
          <el-checkbox
              v-model="isAllSelected"
              :indeterminate="isIndeterminate"
              @change="handleSelectAll"
          >
            {{ $t('wholesaler.OrderList.selectAll') }}（{{ selectedOrders.length }}/{{ selectableOrders.length }}）
          </el-checkbox>
        </div>

        <div v-for="order in orders" :key="order.id" class="order-card">
          <!-- 批量选择 checkbox -->
          <div class="order-checkbox" v-if="canSelectOrder(order)">
            <el-checkbox v-model="selectedOrderIds" :label="order.id" @change="handleOrderSelect(order)"></el-checkbox>
          </div>

          <div class="order-content" @click="viewDetail(order)">
            <div class="order-header">
              <div class="order-no">
                <span class="label">{{ $t('wholesaler.OrderList.line80_text') }}</span>
                <span class="value">{{ order.orderNo }}</span>
                <el-tag v-if="order.orderType === 'AGENT'" size="mini" type="warning" style="margin-left: 8px;">{{ $t('wholesaler.OrderList.line82_text') }}</el-tag>
              </div>
              <div class="order-status" :class="getStatusClass(order.status)">
                {{ getStatusText(order.status) }}
              </div>
            </div>

            <div class="order-body">
              <div class="info-row">
                <span class="label">{{ $t('wholesaler.OrderList.line91_text') }}</span>
                <span class="value">{{ order.customerName }}</span>
              </div>
              <div class="info-row">
                <span class="label">{{ $t('wholesaler.OrderList.line95_text') }}</span>
                <span class="value">{{ order.customerPhone || '-' }}</span>
              </div>
              <div class="info-row">
                <span class="label">{{ $t('wholesaler.OrderList.line99_text') }}</span>
                <div class="items-list">
                  <div v-for="item in order.items" :key="item.id" class="item-row">
                    {{ item.productName }} × {{ item.quantity }}
                  </div>
                </div>
              </div>
              <div class="info-row amount-row">
                <span class="label">{{ $t('wholesaler.OrderList.line107_text') }}</span>
                <span class="value amount">S$ {{ formatAmount(order.totalAmount) }}</span>
                <span class="paid-badge" v-if="order.paidAmount > 0">
                {{ $t('wholesaler.OrderList.paid') }} S$ {{ formatAmount(order.paidAmount) }}
              </span>
              </div>
              <div class="info-row">
                <span class="label">{{ $t('wholesaler.OrderList.line114_text') }}</span>
                <el-tag :type="getPaymentTagType(order.paymentStatus)" size="mini">
                  {{ getPaymentStatusText(order.paymentStatus) }}
                </el-tag>
                <span v-if="order.pendingPaymentAmount > 0" class="pending-amount">
                {{ $t('wholesaler.OrderList.pendingReview') }} S$ {{ formatAmount(order.pendingPaymentAmount) }}
              </span>
              </div>
              <div class="info-row">
                <span class="label">{{ $t('wholesaler.OrderList.line123_text') }}</span>
                <span class="value">{{ formatDate(order.createdAt) }}</span>
              </div>
            </div>

            <div class="order-actions">
              <button class="btn btn-whatsapp" @click.stop="contactCustomerWhatsapp(order)">
                💬 {{ $t('wholesaler.OrderList.contactCustomer') }}
              </button>
              <button class="btn btn-bluetooth" @click.stop="printReceipt(order)" :disabled="printingOrderId === order.id">
                {{ printingOrderId === order.id ? $t('wholesaler.OrderList.printing') : $t('wholesaler.OrderList.bluetoothPrint') }}
              </button>
              <button class="btn btn-cloud-print" @click.stop="cloudPrint(order)" :disabled="cloudPrintingOrderId === order.id">
                {{ cloudPrintingOrderId === order.id ? $t('wholesaler.OrderList.printing') : $t('wholesaler.OrderList.cloudPrint') }}
              </button>
              <button class="btn btn-share" @click.stop="openShareDialog(order)">
                {{ $t('wholesaler.OrderList.share') }}
              </button>
              <button class="btn btn-receipt-share" @click.stop="openReceiptShareDialog(order)">
                {{ $t('wholesaler.OrderList.receiptShare') }}
              </button>
              <button class="btn btn-primary" @click="viewDetail(order)">
                {{ $t('wholesaler.OrderList.line139_text') }}
              </button>
              <template v-if="order.status === 'CONFIRMED' || order.status === 'confirmed'">
                <button class="btn btn-success" @click.stop="handleCreateTask(order)">
                  {{ $t('wholesaler.OrderList.createDeliveryTask') }}
                </button>
              </template>
              <!-- 流水审核按钮：有待审核流水时显示 -->
              <button v-if="order.pendingPaymentRecordIds && order.pendingPaymentRecordIds.length > 0" class="btn btn-info" @click.stop="goToPaymentReview(order)">
                {{ $t('wholesaler.OrderList.paymentReview') }}
              </button>
              <!-- 售后审核按钮：有待审核售后时显示 -->
              <button v-else-if="order.hasPendingAfterSales" class="btn btn-danger" @click.stop="goToAfterSalesReview(order)">
                {{ $t('wholesaler.OrderList.afterSalesReview') }}
              </button>
              <!-- 售后按钮：订单已送达/完成且无未审批售后时显示 -->
              <button v-else-if="canAfterSales(order)" class="btn btn-warning" @click.stop="goToOrderAfterSales(order)">
                {{ $t('wholesaler.OrderList.afterSales') }}
              </button>
              <template v-else-if="order.status === 'DISPATCHED' || order.status === 'dispatched'">
                <span class="status-text">{{ $t('wholesaler.OrderList.dispatched') }}</span>
              </template>
              <template v-else-if="order.status === 'DELIVERED' || order.status === 'delivered'">
                <span class="status-text">{{ $t('wholesaler.OrderList.delivered') }}</span>
              </template>
              <template v-else-if="order.status === 'COMPLETED' || order.status === 'completed'">
                <span class="status-text">{{ $t('wholesaler.OrderList.completed') }}</span>
              </template>
              <template v-else-if="order.status === 'CANCELLED' || order.status === 'cancelled'">
                <span class="status-text">{{ $t('wholesaler.OrderList.cancelled') }}</span>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 订单小票打印 Dialog -->
    <OrderPrintDialog ref="printDialog" />

    <!-- 分享订单 Dialog -->
    <WholesalerShareDialog
        v-if="showShareDialog && shareOrderId"
        type="order"
        :item-id="shareOrderId"
        @close="showShareDialog = false"
    />

    <!-- 创建配送任务 Dialog -->
    <el-dialog
        :title="$t('wholesaler.OrderList.createDeliveryTask')"
        :visible.sync="taskDialogVisible"
        width="420px"
        :before-close="handleCloseDialog">

      <!-- 任务类型选择 -->
      <div class="task-type-selection">
        <div class="section-title">📋 {{ $t('wholesaler.OrderList.taskType') }}</div>
        <el-radio-group v-model="taskType" class="task-radio-group">
          <el-radio-button label="ASSIGNED">👤 {{ $t('wholesaler.OrderList.assignDriver') }}</el-radio-button>
          <el-radio-button label="CONTRACTOR">🎯 {{ $t('wholesaler.OrderList.publishContractor') }}</el-radio-button>
        </el-radio-group>
      </div>

      <!-- 众包任务配置(仅众包模式显示) -->
      <div class="contractor-config" v-if="taskType === 'CONTRACTOR'">
        <el-divider />
        <div class="section-title">⚙️ {{ $t('wholesaler.OrderList.contractorConfig') }}</div>

        <el-form :model="contractorConfig" label-width="100px" size="small">
          <el-form-item :label="$t('wholesaler.OrderList.deliveryFee')">
            <el-input-number
                v-model="contractorConfig.fee"
                :min="0"
                :max="1000"
                :precision="2"
                :step="0.5"
                :placeholder="$t('wholesaler.OrderList.enterDeliveryFee')"
                style="width: 100%"
            />
            <div class="form-tip">{{ $t('wholesaler.OrderList.deliveryFeeTip') }}</div>
          </el-form-item>

          <el-form-item :label="$t('wholesaler.OrderList.estimatedDuration')">
            <el-input-number
                v-model="contractorConfig.estimatedDuration"
                :min="10"
                :max="480"
                :precision="0"
                :step="10"
                :placeholder="$t('wholesaler.OrderList.enterDuration')"
                style="width: 100%"
            />
            <div class="form-tip">{{ $t('wholesaler.OrderList.durationTip') }}</div>
          </el-form-item>

          <el-form-item :label="$t('wholesaler.OrderList.taskRemark')">
            <el-input
                v-model="contractorConfig.remark"
                type="textarea"
                :rows="2"
                :placeholder="$t('wholesaler.OrderList.remarkPlaceholder')"
            />
          </el-form-item>
        </el-form>
      </div>

      <el-divider />

      <!-- 仓库选择 -->
      <div class="warehouse-selection">
        <div class="section-title">📦 {{ $t('wholesaler.OrderList.selectWarehouse') }}</div>
        <div class="warehouse-list">
          <div
              v-for="warehouse in availableWarehouses"
              :key="warehouse.id"
              :class="['warehouse-card', { selected: selectedWarehouseId === warehouse.id, 'insufficient': !isStockSufficient(warehouse) }]"
              @click="isStockSufficient(warehouse) && (selectedWarehouseId = warehouse.id)"
          >
            <div class="warehouse-header">
              <el-radio :value="selectedWarehouseId" :label="warehouse.id"></el-radio>
              <div class="warehouse-info">
                <div class="warehouse-name">
                  {{ warehouse.warehouseName }}
                  <el-tag v-if="warehouse.isDefault" size="mini" type="success" style="margin-left: 8px;">{{ $t('wholesaler.OrderList.defaultWarehouse') }}</el-tag>
                  <el-tag v-if="!isStockSufficient(warehouse)" size="mini" type="danger" style="margin-left: 8px;">{{ $t('wholesaler.OrderList.insufficientStock') }}</el-tag>
                </div>
                <div class="warehouse-address">📍 {{ warehouse.warehouseAddress || $t('wholesaler.OrderList.noAddress') }}</div>
                <!-- 订单商品库存 -->
                <div class="product-stocks" v-if="warehouse.productStocks">
                  <div v-if="warehouse.productStocks.length === 0" class="no-stock">
                    <span class="product-name">⚠️ {{ $t('wholesaler.OrderList.noStockRecord') }}</span>
                  </div>
                  <div
                      v-else
                      v-for="stock in warehouse.productStocks"
                      :key="stock.productId || stock.id"
                      class="stock-item"
                  >
                    <span class="product-name">{{ stock.productName || $t('wholesaler.OrderList.product') }}</span>
                    <span class="stock-qty">{{ $t('wholesaler.OrderList.stock') }}:{{ stock.stockQuantity || 0 }} {{ stock.unit || '' }}</span>
                    <el-tag
                        :type="(stock.stockQuantity || 0) >= (orderQuantityMap[stock.productId] || 0) ? 'success' : 'warning'"
                        size="mini"
                    >
                      {{ $t('wholesaler.OrderList.order') }}:{{ orderQuantityMap[stock.productId] || 0 }}
                    </el-tag>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <el-divider />

      <!-- 配送员选择(仅指派任务显示) -->
      <div class="driver-selection" v-if="taskType === 'ASSIGNED'">
        <div class="section-title">🚚 {{ $t('wholesaler.OrderList.selectDriver') }}</div>
        <div class="driver-list">
          <div
              v-for="driver in availableDrivers"
              :key="driver.id"
              :class="['driver-card', { selected: selectedDriverId === driver.id }]"
              @click="selectedDriverId = driver.id"
          >
            <div class="driver-header">
              <el-radio :value="selectedDriverId" :label="driver.id"></el-radio>
              <div class="driver-info">
                <div class="driver-name">{{ driver.name }}</div>
                <div class="driver-phone">📞 {{ driver.phone }}</div>
                <div class="driver-vehicle" v-if="driver.vehicleType">🚗 {{ driver.vehicleType }} · {{ driver.maxLoad }}kg</div>
              </div>
              <el-tag :type="driver.status === 'IDLE' ? 'success' : 'info'" size="small">
                {{ driver.status === 'IDLE' ? $t('wholesaler.OrderList.idle') : driver.status }}
              </el-tag>
            </div>
          </div>
        </div>
      </div>

      <span slot="footer">
        <el-button @click="handleCloseDialog">{{ $t('common.cancel') }}</el-button>
        <el-button type="primary" @click="handleConfirm">{{ $t('common.confirm') }}</el-button>
      </span>
    </el-dialog>

    <!-- 申请售后弹窗 -->
    <el-dialog
        :title="$t('wholesaler.OrderList.applyAfterSales')"
        :visible.sync="showApplyDialog"
        width="90%"
        class="apply-dialog"
    >
      <div class="apply-info">
        📋 {{ $t('wholesaler.OrderList.order') }}: {{ currentOrder && currentOrder.orderNo }} · {{ $t('wholesaler.OrderList.customer') }}: {{ currentOrder && currentOrder.customerName || $t('wholesaler.OrderList.unknown') }} · S$ {{ formatAmount(currentOrder && currentOrder.totalAmount) }}
      </div>

      <!-- 问题原因 -->
      <div class="form-section">
        <div class="form-label">{{ $t('wholesaler.OrderList.reason') }}</div>
        <div class="reason-tags">
          <span
              v-for="r in reasons"
              :key="r.value"
              class="reason-tag"
              :class="{ active: applyForm.reasonCode === r.value }"
              @click="applyForm.reasonCode = r.value"
          >
            {{ r.icon }} {{ r.label }}
          </span>
        </div>
      </div>

      <!-- 问题描述 -->
      <div class="form-section">
        <div class="form-label">{{ $t('wholesaler.OrderList.description') }}</div>
        <el-input
            v-model="applyForm.reasonText"
            type="textarea"
            :rows="3"
            :placeholder="$t('wholesaler.OrderList.describeProblem')"
            maxlength="500"
            show-word-limit
        ></el-input>
      </div>

      <!-- 上传凭证 -->
      <div class="form-section">
        <div class="form-label">📸 {{ $t('wholesaler.OrderList.uploadProof') }}</div>
        <el-upload
            :action="uploadUrl"
            :headers="uploadHeaders"
            list-type="picture-card"
            :file-list="applyForm.evidenceFiles"
            :on-success="handleUploadSuccess"
            :on-remove="handleUploadRemove"
            :limit="9"
            accept="image/*"
        >
          <i class="el-icon-plus"></i>
        </el-upload>
      </div>

      <span slot="footer">
        <el-button @click="showApplyDialog = false">{{ $t('common.cancel') }}</el-button>
        <el-button type="primary" :disabled="!applyForm.reasonCode" @click="submitApply">{{ $t('wholesaler.OrderList.submitApplication') }}</el-button>
      </span>
    </el-dialog>

    <!-- 批量指派配送员弹窗 -->
    <el-dialog
        :title="$t('wholesaler.OrderList.batchAssignDriver')"
        :visible.sync="batchAssignDialogVisible"
        width="480px"
        :before-close="() => { batchAssignDialogVisible = false }"
    >
      <div class="batch-summary">
        <span>{{ $t('wholesaler.OrderList.selected') }} <strong>{{ selectedOrders.length }}</strong> {{ $t('wholesaler.OrderList.orderUnit') }}</span>
        <span style="margin-left: 16px; color: #999;">{{ $t('wholesaler.OrderList.totalAmount') }} S$ {{ selectedOrders.reduce((sum, o) => sum + (o.totalAmount || 0), 0).toFixed(2) }}</span>
      </div>

      <!-- 配送员选择 -->
      <div class="driver-selection">
        <div class="section-title">🚚 {{ $t('wholesaler.OrderList.selectDriver') }}</div>
        <div class="driver-list">
          <div
              v-for="driver in availableDrivers"
              :key="driver.id"
              :class="['driver-card', { selected: selectedDriverId === driver.id }]"
              @click="selectedDriverId = driver.id"
          >
            <div class="driver-header">
              <el-radio :value="selectedDriverId" :label="driver.id"></el-radio>
              <div class="driver-info">
                <div class="driver-name">{{ driver.name }}</div>
                <div class="driver-phone">📞 {{ driver.phone }}</div>
                <div class="driver-vehicle" v-if="driver.vehicleType">🚗 {{ driver.vehicleType }} · {{ driver.maxLoad }}kg</div>
              </div>
              <el-tag :type="driver.status === 'IDLE' ? 'success' : 'info'" size="small">
                {{ driver.status === 'IDLE' ? $t('wholesaler.OrderList.idle') : driver.status }}
              </el-tag>
            </div>
          </div>
        </div>
      </div>

      <span slot="footer">
        <el-button @click="batchAssignDialogVisible = false">{{ $t('common.cancel') }}</el-button>
        <el-button type="primary" :loading="batchAssigning" @click="confirmBatchAssign">
          {{ batchAssigning ? $t('wholesaler.OrderList.assigning') : $t('wholesaler.OrderList.confirmAssign') }}
        </el-button>
      </span>
    </el-dialog>

    <!-- 小票分享对话框 -->
    <ReceiptShareDialog ref="receiptShareDialog" />

    <!-- 分页 -->
    <div class="pagination-wrap" v-if="total > 0">
      <el-pagination
          background
          layout="total, prev, pager, next"
          :total="total"
          :page-size="pageSize"
          :current-page="currentPage"
          @current-change="handlePageChange"
      />
    </div>

    <!-- 底部导航 -->
    <nav class="bottom-nav">
      <button class="nav-item" @click="navigateTo('home')">
        <span class="nav-item-icon">🏠</span>
        <span>{{ $t('navigation.home') }}</span>
      </button>
      <button class="nav-item" @click="navigateTo('products')">
        <span class="nav-item-icon">📦</span>
        <span>{{ $t('navigation.products') }}</span>
      </button>
      <button class="nav-item" @click="navigateTo('wholesale')">
        <span class="nav-item-icon">💰</span>
        <span>{{ $t('wholesaler.wholesalePrice') }}</span>
      </button>
      <button class="nav-item active">
        <span class="nav-item-icon">📋</span>
        <span>{{ $t('navigation.orders') }}</span>
      </button>
      <button class="nav-item" @click="navigateTo('profile')">
        <span class="nav-item-icon">👤</span>
        <span>{{ $t('navigation.profile') }}</span>
      </button>
    </nav>
  </div>
</template>

<script>
import api from '@/services/api';
import OrderPrintDialog from '@/components/OrderPrintDialog.vue';
import WholesalerShareDialog from '@/components/WholesalerShareDialog.vue';
import ReceiptShareDialog from '@/components/ReceiptShareDialog.vue';
import { getWholesalerId } from '@/utils/wholesaler';
import { applyAfterSales } from '@/services/afterSales';

export default {
  name: 'OrderList',
  components: {
    OrderPrintDialog,
    WholesalerShareDialog,
    ReceiptShareDialog
  },
  data() {
    return {
      orders: [],
      loading: false,
      searchKeyword: '',
      // 分页
      currentPage: 1,
      pageSize: 20,
      total: 0,
      taskDialogVisible: false,
      taskType: 'ASSIGNED', // ASSIGNED 或 CONTRACTOR
      contractorConfig: {
        fee: 15.00, // 默认配送费
        estimatedDuration: 60, // 默认 60 分钟
        remark: ''
      },
      availableWarehouses: [],
      availableDrivers: [],
      selectedWarehouseId: '',
      selectedDriverId: null,
      currentOrder: null,
      orderQuantityMap: {},
      resolveDialog: null,
      rejectDialog: null,
      printingOrderId: null,
      cloudPrintingOrderId: null,
      pendingPaymentCount: 0,
      showApplyDialog: false,
      applyForm: {
        reasonCode: '',
        reasonText: '',
        evidenceFiles: []
      },
      reasons: [
        { value: 'DAMAGED', icon: '💔', label: this.$t('wholesaler.OrderList.reasonDamaged') },
        { value: 'WRONG_ITEM', icon: '❌', label: this.$t('wholesaler.OrderList.reasonWrongItem') },
        { value: 'NOT_RECEIVED', icon: '📦', label: this.$t('wholesaler.OrderList.reasonNotReceived') },
        { value: 'WRONG_SPEC', icon: '📐', label: this.$t('wholesaler.OrderList.reasonWrongSpec') },
        { value: 'MISSING', icon: '🔍', label: this.$t('wholesaler.OrderList.reasonMissing') },
        { value: 'NOT_WANTED', icon: '🙅', label: this.$t('wholesaler.OrderList.reasonNotWanted') }
      ],
      // 批量选择
      selectedOrderIds: [],
      selectedOrders: [],
      batchAssignDialogVisible: false,
      batchAssigning: false,
      // 分享功能
      showShareDialog: false,
      shareOrderId: null,
      // 状态筛选
      filterStatus: '',
      // 零负库存开关
      allowNegativeStock: false,
      statusOptions: [
        { value: '', label: this.$t('wholesaler.OrderList.all') },
        { value: 'CONFIRMED', label: this.$t('wholesaler.OrderList.confirmed') },
        { value: 'PACKING', label: this.$t('wholesaler.OrderList.packing') },
        { value: 'READY_TO_SHIP', label: this.$t('wholesaler.OrderList.readyToShip') },
        { value: 'DISPATCHED', label: this.$t('wholesaler.OrderList.dispatched') },
        { value: 'DELIVERED', label: this.$t('wholesaler.OrderList.delivered') },
        { value: 'COMPLETED', label: this.$t('wholesaler.OrderList.completed') },
        { value: 'CANCELLED', label: this.$t('wholesaler.OrderList.cancelled') }
      ]
    };
  },
  created() {
    this.loadOrders();
  },
  computed: {
    uploadUrl() {
      return `${process.env.VUE_APP_API_URL}/files/upload-image`;
    },
    uploadHeaders() {
      const token = localStorage.getItem('auth_token');
      return token ? { Authorization: `Bearer ${token}` } : {};
    },
    // 可选中的订单（已确认状态）
    selectableOrders() {
      return this.orders.filter(o => o.status === 'CONFIRMED' || o.status === 'confirmed');
    },
    // 是否全选
    isAllSelected: {
      get() {
        return this.selectableOrders.length > 0 &&
            this.selectedOrders.length === this.selectableOrders.length;
      },
      set(val) {
        // setter 用于处理全选切换
      }
    },
    // 是否部分选中
    isIndeterminate() {
      return this.selectedOrders.length > 0 &&
          this.selectedOrders.length < this.selectableOrders.length;
    },
    // 是否可以批量打包（选中的订单都是已确认状态）
    canBatchPack() {
      return this.selectedOrders.length > 0 &&
          this.selectedOrders.every(o => o.status === 'CONFIRMED');
    }
  },
  beforeDestroy() {},
  methods: {
    async loadOrders() {
      this.loading = true;
      try {
        const params = {
          wholesalerId: getWholesalerId(),
          page: this.currentPage,
          size: this.pageSize
        };
        if (this.searchKeyword) {
          params.keyword = this.searchKeyword;
        }
        if (this.filterStatus) {
          params.status = this.filterStatus;
        }

        const response = await api.get('/orders', { params });
        this.orders = response?.items || response?.data?.items || [];
        this.total = response?.total || 0;
        this.pendingPaymentCount = response?.pendingPaymentCount || response?.data?.pendingPaymentCount || 0;
      } catch (error) {
        console.error('加载订单失败:', error);
        this.$message.error('加载失败');
      } finally {
        this.loading = false;
      }
    },
    canCancel(order) {
      return order.canCancel === true;
    },
    canAfterSales(order) {
      // 后端已计算 canAfterSales 字段
      return order.canAfterSales === true;
    },
    goToOrderAfterSales(order) {
      this.currentOrder = order;
      this.showApplyDialog = true;
    },
    goToAfterSalesReview(order) {
      if (order.pendingAfterSalesId) {
        this.$router.push(`/wholesaler/after-sales/${order.pendingAfterSalesId}/review`);
      }
    },
    goToPaymentReview(order) {
      this.$router.push(`/wholesaler/orders/${order.id}?tab=payment`);
    },
    async batchApplyAfterSales() {
      if (this.selectedIds.length === 0) return;
      this.$router.push(`/wholesaler/after-sales`);
    },

    // ===== 批量选择方法 =====
    canSelectOrder(order) {
      return order.status === 'CONFIRMED' || order.status === 'confirmed';
    },
    handleOrderSelect(order) {
      const idx = this.selectedOrderIds.indexOf(order.id);
      if (idx > -1 && !this.selectedOrderIds.includes(order.id)) {
        // removed
      }
      this.selectedOrders = this.orders.filter(o => this.selectedOrderIds.includes(o.id));
    },
    clearSelection() {
      this.selectedOrderIds = [];
      this.selectedOrders = [];
    },
    handleSelectAll(val) {
      if (val) {
        // 全选：将所有可选中的订单 ID 加入选中列表
        const selectableIds = this.selectableOrders.map(o => o.id);
        this.selectedOrderIds = [...new Set([...this.selectedOrderIds, ...selectableIds])];
        this.selectedOrders = this.orders.filter(o => this.selectedOrderIds.includes(o.id));
      } else {
        // 取消全选：移除所有可选中订单的 ID
        const selectableIds = this.selectableOrders.map(o => o.id);
        this.selectedOrderIds = this.selectedOrderIds.filter(id => !selectableIds.includes(id));
        this.selectedOrders = this.orders.filter(o => this.selectedOrderIds.includes(o.id));
      }
    },
    async handleBatchPack() {
      if (this.selectedOrders.length === 0) {
        this.$message.warning('请先选择订单');
        return;
      }

      try {
        await this.$confirm(this.$t('wholesaler.OrderList.confirmBatchPack', { count: this.selectedOrders.length }), this.$t('wholesaler.OrderList.batchPack'), {
          confirmButtonText: this.$t('wholesaler.OrderList.confirm'),
          cancelButtonText: this.$t('wholesaler.OrderList.cancel'),
          type: 'warning'
        });

        const orderIds = this.selectedOrders.map(o => o.id);
        const response = await api.post('/orders/batch-pack', { orderIds });

        if (response.data.success) {
          this.$message.success(`批量打包成功：${response.data.successCount} 个订单已标记为打包中`);
          this.clearSelection();
          this.loadOrders();
        } else {
          this.$message.error(response.data.message || '批量打包失败');
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('批量打包失败:', error);
          this.$message.error('批量打包失败');
        }
      }
    },
    async handleBatchAssign() {
      if (this.selectedOrders.length === 0) {
        this.$message.warning('请先选择订单');
        return;
      }
      // 复用现有的创建任务逻辑，但针对多个订单
      // 简化版：打开对话框选择仓库和配送员
      const wholesalerId = getWholesalerId() ?? 1;

      // 获取仓库列表（简化：只用默认仓库）
      try {
        const wholesalerRes = await api.get('/wholesalers/' + wholesalerId);
        const wholesalerInfo = Array.isArray(wholesalerRes) ? wholesalerRes[0] : (wholesalerRes.data || wholesalerRes);

        const shopWarehouse = {
          id: 'default_shop',
          warehouseName: wholesalerInfo.companyName || wholesalerInfo.shopName || this.$t('wholesaler.OrderList.shopWarehouse'),
          warehouseAddress: wholesalerInfo.address || this.$t('wholesaler.OrderList.shopAddress'),
          latitude: wholesalerInfo.latitude || null,
          longitude: wholesalerInfo.longitude || null,
          isDefault: true,
          productStocks: []
        };

        // 获取配送员列表
        const driversRes = await api.get('/delivery/drivers/all', { params: { wholesalerId } });
        const drivers = Array.isArray(driversRes) ? driversRes : (driversRes.content || driversRes.data?.content || driversRes.data || []);

        if (drivers.length === 0) {
          this.$message.warning('暂无可用配送员，请先添加配送员');
          return;
        }

        this.availableWarehouses = [shopWarehouse];
        this.availableDrivers = drivers;
        this.selectedWarehouseId = 'default_shop';
        this.selectedDriverId = null;
        this.batchAssignDialogVisible = true;
      } catch (error) {
        console.error('加载数据失败:', error);
        this.$message.error('加载数据失败');
      }
    },
    async confirmBatchAssign() {
      if (!this.selectedDriverId) {
        this.$message.warning('请选择配送员');
        return;
      }

      this.batchAssigning = true;
      try {
        const wholesalerId = getWholesalerId() ?? 1;
        const warehouse = this.availableWarehouses.find(w => w.id === this.selectedWarehouseId);
        const driver = this.availableDrivers.find(d => d.id === this.selectedDriverId);

        const orderIds = this.selectedOrders.map(o => o.id);

        const response = await api.post('/delivery/tasks/batch-assign', {
          wholesalerId,
          driverId: driver.id,
          orderIds,
          warehouseId: this.selectedWarehouseId !== 'default_shop' ? this.selectedWarehouseId : 0,
          pickupAddress: warehouse.warehouseAddress || this.$t('wholesaler.OrderList.warehouse'),
          pickupGpsLat: warehouse.latitude || null,
          pickupGpsLng: warehouse.longitude || null,
          driverName: driver.name,
          remark: ''
        });

        const result = response.data || response;
        this.$message.success(`批量指派完成：成功 ${result.success} 个，失败 ${result.failed} 个`);

        if (result.errors && result.errors.length > 0) {
          console.warn('部分订单指派失败:', result.errors);
        }

        this.batchAssignDialogVisible = false;
        this.clearSelection();
        this.loadOrders();
      } catch (error) {
        console.error('批量指派失败:', error);
        this.$message.error('批量指派失败: ' + (error.response?.data?.message || error.message));
      } finally {
        this.batchAssigning = false;
      }
    },

    /**
     * 云打印订单小票
     */
    async cloudPrint(order) {
      this.cloudPrintingOrderId = order.id;
      try {
        const response = await api.post(`/orders/${order.id}/print`);
        // api 拦截器已解包 data，response 就是 { success, message }
        if (response && response.success) {
          this.$message.success(response.message || '云打印任务已发送');
        } else {
          this.$message.error('云打印失败: ' + (response?.message || '未知错误'));
        }
      } catch (error) {
        console.error('云打印失败:', error);
        this.$message.error('云打印失败: ' + (error.response?.data?.message || error.message));
      } finally {
        this.cloudPrintingOrderId = null;
      }
    },

    /**
     * 打印订单小票（蓝牙打印）
     */
    async printReceipt(order) {
      try {
        // 获取完整的订单数据（包含 items）
        const response = await api.get(`/orders/${order.id}`);
        const fullOrder = response.data || response;

        // 获取应收款数据
        let receivableData = null;
        const customerId = fullOrder.customerId || fullOrder.customer?.id;
        if (customerId) {
          try {
            const receivableRes = await api.get(`/report/receivable/for-print/${customerId}`, {
              params: { wholesalerId: getWholesalerId() }
            });
            receivableData = receivableRes.data || receivableRes;
          } catch (e) {
            console.warn('获取应收款数据失败:', e);
          }
        }

        // 获取蓝牙打印机实例
        const { getPrinter } = await import('@/services/bluetooth-printer');
        const printer = getPrinter();

        // 连接打印机
        const connectResult = await printer.connect();
        if (!connectResult.success) {
          this.$message.error('蓝牙连接失败: ' + connectResult.error);
          return;
        }

        // 打印订单小票（printSingaporeOrder 需要 order, customer, receivableData）
        await printer.printSingaporeOrder(fullOrder, null, receivableData);
        this.$message.success('打印成功');

      } catch (error) {
        console.error('打印失败:', error);
        this.$message.error('打印失败: ' + (error.response?.data?.message || error.message));
      }
    },

    openShareDialog(order) {
      this.shareOrderId = order.id;
      this.showShareDialog = true;
    },

    contactCustomerWhatsapp(order) {
      if (!order.customerWhatsapp) {
        this.$message.warning(this.$t('wholesaler.OrderList.noCustomerWhatsapp'));
        return;
      }
      const orderNo = order.orderNo || '';
      const message = `你好，关于订单 ${orderNo}，我想和你沟通一下...`;
      const url = `https://wa.me/${order.customerWhatsapp}?text=${encodeURIComponent(message)}`;
      window.open(url, '_blank');
    },

    async openReceiptShareDialog(order) {
      try {
        const response = await api.get(`/orders/${order.id}`);
        const fullOrder = response.data || response;
        
        // 获取应收款数据（与打印逻辑一致）
        let receivableData = null;
        const customerId = fullOrder.customerId || fullOrder.customer?.id;
        if (customerId) {
          try {
            const receivableRes = await api.get(`/report/receivable/for-print/${customerId}`, {
              params: { wholesalerId: getWholesalerId() }
            });
            receivableData = receivableRes.data || receivableRes;
          } catch (e) {
            console.warn('获取应收款数据失败:', e);
          }
        }
        
        // 直接生成图片，不弹窗
        await this.$refs.receiptShareDialog.directGenerate(fullOrder, {}, receivableData);
      } catch (error) {
        console.error('生成小票图片失败:', error);
        this.$message.error('生成小票图片失败');
      }
    },


    handleCloseDialog() {
      this.taskDialogVisible = false;
      if (this.rejectDialog) {
        this.rejectDialog(new Error('cancel'));
        this.resolveDialog = null;
        this.rejectDialog = null;
      }
    },

    handleSearch() {
      this.currentPage = 1;
      this.loadOrders();
    },

    setFilterStatus(status) {
      this.filterStatus = status;
      this.currentPage = 1;
      this.loadOrders();
    },

    handlePageChange(page) {
      this.currentPage = page;
      this.loadOrders();
    },

    getStatusClass(status) {
      const classes = {
        'PENDING': 'status-pending',
        'CONFIRMED': 'status-confirmed',
        'PACKING': 'status-packing',
        'READY_TO_SHIP': 'status-ready',
        'DISPATCHED': 'status-dispatched',
        'DELIVERED': 'status-delivered',
        'COMPLETED': 'status-completed',
        'CANCELLED': 'status-cancelled'
      };
      return classes[status] || '';
    },

    getStatusText(status) {
      const texts = {
        'PENDING': this.$t('wholesaler.OrderList.pending'),
        'CONFIRMED': this.$t('wholesaler.OrderList.confirmed'),
        'PACKING': this.$t('wholesaler.OrderList.packing'),
        'READY_TO_SHIP': this.$t('wholesaler.OrderList.readyToShip'),
        'DISPATCHED': this.$t('wholesaler.OrderList.dispatched'),
        'DELIVERED': this.$t('wholesaler.OrderList.delivered'),
        'COMPLETED': this.$t('wholesaler.OrderList.completed'),
        'CANCELLED': this.$t('wholesaler.OrderList.cancelled')
      };
      return texts[status] || status;
    },

    getPaymentTagType(status) {
      const m = { paid: 'success', underpaid: 'warning', overpaid: 'info' };
      return m[status] || 'warning';
    },
    getPaymentStatusText(status) {
      const m = { paid: this.$t('wholesaler.OrderList.paid'), underpaid: this.$t('wholesaler.OrderList.underpaid'), overpaid: this.$t('wholesaler.OrderList.overpaid') };
      return m[status] || this.$t('wholesaler.OrderList.unknown');
    },
    getStatusType(status) {
      const types = {
        'PENDING': 'warning',
        'CONFIRMED': 'success',
        'PACKING': 'warning',
        'READY_TO_SHIP': 'success',
        'DISPATCHED': 'primary',
        'DELIVERED': 'success',
        'COMPLETED': 'success',
        'CANCELLED': 'info'
      };
      return types[status] || 'info';
    },

    formatDate(dateStr) {
      if (!dateStr) return '-';
      const date = new Date(dateStr);
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    formatAmount(amount) {
      return (amount || 0).toFixed(2);
    },

    viewDetail(order) {
      this.$router.push(`/wholesaler/orders/${order.id}`);
    },
    goToAfterSales() {
      this.$router.push('/wholesaler/after-sales');
    },

    handleUploadSuccess(response, file, fileList) {
      this.applyForm.evidenceFiles = fileList;
    },
    handleUploadRemove(file, fileList) {
      this.applyForm.evidenceFiles = fileList;
    },
    async submitApply() {
      if (!this.applyForm.reasonCode) {
        this.$message.warning('请选择问题原因');
        return;
      }
      try {
        const imageUrls = this.applyForm.evidenceFiles
            .filter(f => f.response && f.response.url)
            .map(f => f.response.url);

        await applyAfterSales({
          orderId: this.currentOrder.id,
          orderNo: this.currentOrder.orderNo,
          reasonCode: this.applyForm.reasonCode,
          reasonText: this.applyForm.reasonText,
          evidenceUrls: JSON.stringify(imageUrls)
        });

        this.$message.success('售后申请已提交');
        this.showApplyDialog = false;
        this.applyForm = { reasonCode: '', reasonText: '', evidenceFiles: [] };
        await this.loadOrders();
      } catch (e) {
        this.$message.error('提交失败: ' + (e.response?.data?.message || e.message));
      }
    },

    async createDeliveryTask(order) {
      try {
        console.log('🟠 开始创建任务,订单(653行）:', order)
        const wholesalerId = getWholesalerId() ?? 1

        const driversRes = await api.get('/delivery/drivers/all', {
          params: { wholesalerId }
        });

        // 响应拦截器已经返回 response.data,可能是数组或对象
        const drivers = Array.isArray(driversRes) ? driversRes :
            (driversRes.content || driversRes.data?.content || driversRes.data || []);

        if (drivers.length === 0) {
          this.$message.warning('暂无可用配送员,请先添加配送员');
          return;
        }

        // 显示 Dialog
        this.availableDrivers = drivers;
        this.selectedDriverId = null;
        this.currentOrder = order;
        this.driverDialogVisible = true;

        console.log('🔵 Dialog 已打开')
        console.log('🔵 resolveDialog 设置前:', this.resolveDialog)
        // 返回 Promise 等待用户选择
        return new Promise((resolve, reject) => {
          this.resolveDialog = resolve;
          this.rejectDialog = reject;
          console.log('🔵 resolveDialog 设置后:', this.resolveDialog)
          console.log('🔵 rejectDialog 设置后:', this.rejectDialog)
        });

      } catch (error) {
        this.$message.error('加载配送员失败');
        throw error;
      }
    },

    async handleCreateTask(order) {
      console.log('🟠 开始创建任务,订单ID:', order.id)

      try {
        // 根据订单ID重新加载完整订单数据（包含GPS坐标）
        const orderDetailRes = await api.get('/orders/' + order.id)
        const fullOrder = orderDetailRes?.data || orderDetailRes
        console.log('📦 完整订单数据:', fullOrder)
        this.currentOrder = fullOrder

        const wholesalerId = getWholesalerId() ?? 1

        // 获取批发商信息(包含默认店铺仓库)
        const wholesalerRes = await api.get('/wholesalers/' + wholesalerId)
        const wholesalerInfo = Array.isArray(wholesalerRes) ? wholesalerRes[0] : (wholesalerRes.data || wholesalerRes)

        console.log('🔵 批发商信息:', wholesalerInfo)
        console.log('🔵 批发商地址:', wholesalerInfo?.address)
        console.log('🔵 批发商公司名:', wholesalerInfo?.companyName)

        // 读取零负库存开关
        this.allowNegativeStock = Boolean(wholesalerInfo?.allowNegativeStock)
        console.log('📦 零负库存开关:', this.allowNegativeStock)

        // 获取订单商品列表(后端已返回完整数据)
        const orderItems = fullOrder.items || []
        console.log('🔵 订单商品:', orderItems)

        // 构建订单商品数量映射(使用 productId)
        const orderQuantityMap = {}
        orderItems.forEach(item => {
          if (item.productId) {
            orderQuantityMap[item.productId] = item.quantity || 0
          }
        })
        this.orderQuantityMap = orderQuantityMap
        console.log('🔵 订单商品数量映射:', orderQuantityMap)

        // 构建仓库列表
        let allWarehouses = []

        // 添加默认店铺仓库
        if (wholesalerInfo) {
          const shopWarehouse = {
            id: 'default_shop',
            warehouseName: wholesalerInfo.companyName || wholesalerInfo.shopName || this.$t('wholesaler.OrderList.shopWarehouse'),
            warehouseAddress: wholesalerInfo.address || this.$t('wholesaler.OrderList.shopAddress'),
            warehouseLocation: wholesalerInfo.location || '',
            warehousePhone: wholesalerInfo.phone || wholesalerInfo.contactPhone || '',
            latitude: wholesalerInfo.latitude || null,
            longitude: wholesalerInfo.longitude || null,
            isDefault: true,
            productStocks: [] // 订单商品库存
          }
          allWarehouses.push(shopWarehouse)
          console.log('🔵 店铺仓库:', shopWarehouse)
        } else {
          console.warn('⚠️ 未获取到批发商信息')
        }

        // 获取其他仓库列表
        const warehousesRes = await api.get('/warehouse/list', {
          params: { wholesalerId }
        })
        // 仓库数据可能是数组,或者在 data 字段中
        let otherWarehouses = []
        if (Array.isArray(warehousesRes)) {
          otherWarehouses = warehousesRes
        } else if (warehousesRes.data && Array.isArray(warehousesRes.data)) {
          otherWarehouses = warehousesRes.data
        } else if (warehousesRes.data?.data && Array.isArray(warehousesRes.data.data)) {
          otherWarehouses = warehousesRes.data.data
        }

        // 为每个仓库添加库存信息
        otherWarehouses = otherWarehouses.map(w => ({
          ...w,
          productStocks: [] // 订单商品库存
        }))

        console.log('🔵 其他仓库:', otherWarehouses)
        console.log('🔵 其他仓库数量:', otherWarehouses.length)

        // 合并仓库列表
        allWarehouses = [...allWarehouses, ...otherWarehouses]

        // 从后端加载每个仓库的库存数据
        try {
          console.log('🔵 开始加载库存数据,批发商 ID:', wholesalerId)

          // 查询所有订单商品的库存
          const productIds = orderItems.map(item => item.productId).filter(id => id != null)
          console.log('🔵 商品 IDs:', productIds)

          // 调用后端 API 获取库存 - 使用 paramsSerializer 避免数组被序列化为 productIds[]
          const stockRes = await api.get('/warehouse/stocks', {
            params: {
              wholesalerId: wholesalerId,
              productIds: productIds
            },
            paramsSerializer: params => {
              const parts = []
              // wholesalerId
              parts.push(`wholesalerId=${params.wholesalerId}`)
              // productIds - 每个都作为单独的 productIds 参数
              if (Array.isArray(params.productIds)) {
                params.productIds.forEach(id => {
                  parts.push(`productIds=${id}`)
                })
              }
              return parts.join('&')
            }
          })

          console.log('🔵 库存 API 响应:', stockRes)

          // 解析库存数据
          let allStocks = []
          if (Array.isArray(stockRes)) {
            allStocks = stockRes
          } else if (stockRes.data) {
            allStocks = stockRes.data.data || stockRes.data.items || stockRes.data
          }

          console.log('🔵 库存数据:', allStocks)

          // 为每个仓库分配库存数据
          for (const warehouse of allWarehouses) {
            if (warehouse.id === 'default_shop') {
              // ⭐ 店铺库存直接用商品的 shopStockQuantity，不查 product_warehouse_stock
              warehouse.productStocks = orderItems.map(item => ({
                productId: item.productId,
                productName: item.productName,
                stockQuantity: item.shopStockQuantity || 0,
                unit: item.unit || ''
              }));
            } else {
              // 其他仓库(匹配 warehouseId)
              warehouse.productStocks = allStocks
                  .filter(s => s.warehouseId === warehouse.id)
                  .map(s => ({
                    ...s,
                    productName: orderItems.find(item => item.productId === s.productId)?.productName || '商品'
                  }))
            }
            console.log('🔵 仓库', warehouse.warehouseName, '库存:', warehouse.productStocks)
          }
        } catch (error) {
          console.error('❌ 加载库存失败:', error)
          // 加载失败时,显示订单商品但库存为 0
          for (const warehouse of allWarehouses) {
            if (warehouse.id === 'default_shop') {
              // 店铺仓库使用 shopStockQuantity
              warehouse.productStocks = orderItems.map(item => ({
                productId: item.productId,
                productName: item.productName,
                stockQuantity: item.shopStockQuantity || 0,
                unit: item.unit || ''
              }));
            } else {
              warehouse.productStocks = orderItems.map(item => ({
                productId: item.productId,
                productName: item.productName,
                stockQuantity: 0
              }));
            }
          }
        }

        console.log('🔵 仓库 API 完整响应:', warehousesRes)
        console.log('🔵 仓库列表:', allWarehouses)
        console.log('🔵 仓库数量:', allWarehouses.length)

        // 获取配送员列表
        const driversRes = await api.get('/delivery/drivers/all', {
          params: { wholesalerId }
        })
        const drivers = Array.isArray(driversRes) ? driversRes :
            (driversRes.content || driversRes.data?.content || driversRes.data || [])

        if (drivers.length === 0) {
          this.$message.warning('暂无可用配送员,请先添加配送员')
          return
        }

        // 显示统一的对话框(仓库选择 + 配送员选择)
        this.availableWarehouses = allWarehouses
        this.availableDrivers = drivers
        // 默认选择库存充足的仓库
        const sufficientWarehouse = allWarehouses.find(w => this.isStockSufficient(w))
        this.selectedWarehouseId = sufficientWarehouse?.id || allWarehouses[0]?.id || ''
        this.selectedDriverId = null
        this.currentOrder = fullOrder  // 使用按ID加载的完整数据，不用列表数据
        this.taskDialogVisible = true

        // 返回 Promise 等待用户确认
        return new Promise((resolve, reject) => {
          this.resolveDialog = resolve
          this.rejectDialog = reject
        })
      } catch (error) {
        console.error('❌ 创建失败:', error)
        if (error !== 'cancel') {
          this.$message.error('创建失败:' + (error.response?.data?.message || error.message))
        }
        throw error
      }
    },

    async createTaskWithWarehouse(order, warehouse) {
      const driver = await this.createDeliveryTask(order)
      if (!driver) return

      const wholesalerId = getWholesalerId() ?? 1

      console.log('🟠 创建任务,参数:', {
        wholesalerId,
        driverId: driver.id,
        orderId: order.id,
        customerId: order.customerId || order.customer_id || null,
        pickupAddress: warehouse.warehouseAddress || this.$t('wholesaler.OrderList.warehouse'),
        pickupContact: warehouse.warehouseContact || '',
        pickupPhone: warehouse.warehousePhone || ''
      })

      const taskRes = await api.post('/delivery/tasks/assign', {
        wholesalerId,
        driverId: driver.id,
        orderId: order.id,
        customerId: order.customerId || order.customer_id || null,
        pickupAddress: warehouse.warehouseAddress || this.$t('wholesaler.OrderList.warehouse'),
        pickupContact: warehouse.warehouseContact || '',
        pickupPhone: warehouse.warehousePhone || '',
        remark: ''
      })

      console.log('🟢 任务创建成功:', taskRes)
      this.$message.success('配送任务已创建!')
      this.loadOrders()
    },

    async handleConfirm() {
      console.log('🔵 handleConfirm 被点击了!')
      console.log('🔵 taskType:', this.taskType)
      console.log('🔵 selectedWarehouseId:', this.selectedWarehouseId)
      console.log('🔵 selectedDriverId:', this.selectedDriverId)

      const warehouse = this.availableWarehouses.find(w => w.id === this.selectedWarehouseId)
      if (!warehouse) {
        this.$message.warning('请选择仓库')
        return
      }

      // 检查库存是否充足
      if (!this.isStockSufficient(warehouse)) {
        this.$message.warning('该仓库库存不足，请选择其他仓库')
        return
      }

      try {
        const wholesalerId = getWholesalerId() ?? 1

        // 根据任务类型创建不同的任务
        if (this.taskType === 'CONTRACTOR') {
          // 发布众包任务
          const contractorData = {
            wholesalerId,
            orderId: this.currentOrder.id,
            warehouseId: this.selectedWarehouseId !== 'default_shop' ? this.selectedWarehouseId : 0,
            pickupAddress: warehouse.warehouseAddress || this.$t('wholesaler.OrderList.warehouse'),
            deliveryAddress: this.currentOrder.deliveryAddress || this.$t('wholesaler.OrderList.customerAddress'),
            pickupContact: warehouse.warehouseContact || warehouse.phone || '',
            pickupPhone: warehouse.warehousePhone || warehouse.contactPhone || '',
            deliveryContact: this.currentOrder.customerName || '',
            deliveryPhone: this.currentOrder.customerPhone || '',
            fee: this.contractorConfig.fee,
            estimatedDuration: this.contractorConfig.estimatedDuration,
            remark: this.contractorConfig.remark || this.currentOrder.remark || '',
            // GPS 坐标
            pickupGpsLat: warehouse.latitude || null,
            pickupGpsLng: warehouse.longitude || null,
            deliveryGpsLat: this.currentOrder.deliveryAddressLat || null,
            deliveryGpsLng: this.currentOrder.deliveryAddressLng || null
          }
          console.log('🔵 发布众包任务，请求数据:', contractorData)

          await api.post('/delivery/tasks/contractor', contractorData)
          this.$message.success(`众包任务已发布，配送费￥${this.contractorConfig.fee}，等待配送员抢单!`)
        } else {
          // 指派给配送员
          if (!this.selectedDriverId) {
            this.$message.warning('请选择配送员')
            return
          }
          const driver = this.availableDrivers.find(d => d.id === this.selectedDriverId)

          await api.post('/delivery/tasks/assign', {
            wholesalerId,
            driverId: driver.id,
            orderId: this.currentOrder.id,
            customerId: this.currentOrder.customerId || this.currentOrder.customer_id || null,
            warehouseId: this.selectedWarehouseId !== 'default_shop' ? this.selectedWarehouseId : 0,
            pickupAddress: warehouse.warehouseAddress || this.$t('wholesaler.OrderList.warehouse'),
            pickupContact: warehouse.warehouseContact || warehouse.phone || '',
            pickupPhone: warehouse.warehousePhone || warehouse.contactPhone || '',
            // GPS 坐标
            pickupGpsLat: warehouse.latitude || null,
            pickupGpsLng: warehouse.longitude || null,
            deliveryGpsLat: this.currentOrder.deliveryAddressLat || null,
            deliveryGpsLng: this.currentOrder.deliveryAddressLng || null,
            remark: ''
          })
          this.$message.success('配送任务已创建!')

          if (this.resolveDialog) {
            this.resolveDialog(driver)
            this.resolveDialog = null
            this.rejectDialog = null
          }
        }

        this.taskDialogVisible = false
        this.loadOrders()
      } catch (error) {
        console.error('❌ 创建失败:', error)
        this.$message.error('创建失败:' + (error.response?.data?.message || error.message))
      }
    },

    /** 检查仓库库存是否满足订单需求 */
    isStockSufficient(warehouse) {
      // 零负库存开关开启时，跳过库存检查
      if (this.allowNegativeStock) return true
      if (!warehouse || !warehouse.productStocks) return true
      for (const stock of warehouse.productStocks) {
        const required = this.orderQuantityMap[stock.productId] || 0
        const available = stock.stockQuantity || 0
        if (available < required) return false
      }
      return true
    },

    async cancelOrder(order) {
      try {
        await this.$confirm(`确定要取消订单 ${order.orderNo} 吗?`, this.$t('wholesaler.OrderList.tip'), {
          confirmButtonText: this.$t('wholesaler.OrderList.confirm'),
          cancelButtonText: this.$t('wholesaler.OrderList.cancel'),
          type: 'warning'
        });

        await api.post(`/orders/${order.id}/cancel`);
        this.$message.success('订单已取消');
        this.loadOrders();
      } catch {
        // 用户取消
      }
    },

    navigateTo(tab) {
      const routes = {
        home: '/wholesaler/home',
        products: '/wholesaler/product',
        wholesale: '/wholesaler/wholesale-price-list',
        profile: '/my-profile'
      };
      if (routes[tab]) {
        this.$router.push(routes[tab]);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.mobile-order-list {
  min-height: 100vh;
  background: #f5f5f5;
  padding-bottom: 120px;
}

/* 分页 */
.pagination-wrap {
  display: flex;
  justify-content: center;
  padding: 20px 16px;
  background: #fff;
  margin-top: 12px;
}

.page-header {
  background: white;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;

  h1 {
    font-size: 18px;
    color: #333;
    margin: 0;
  }
}

/* 返回按钮适配 */
.page-header .back-btn,
.page-header button[onclick*="goBack"],
.page-header .header-back {
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: background 0.2s;
}

.page-header .back-btn:active,
.page-header button:active,
.page-header .header-back:active {
  background: rgba(0, 0, 0, 0.05);
}

/* 返回按钮适配 */
.page-header .back-btn,
.page-header button[onclick*="goBack"],
.page-header .header-back {
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: background 0.2s;
}

.page-header .back-btn:active,
.page-header button:active,
.page-header .header-back:active {
  background: rgba(0, 0, 0, 0.05);
}

.stat-num {
  display: block;
  font-size: 22px;
  font-weight: 700;
  color: #333;
}
.stat-item.stat-warn .stat-num { color: #f59e0b; }
.stat-label {
  font-size: 12px;
  color: #999;
}

.search-bar {
  padding: 12px;
  background: white;
  margin-bottom: 12px;
}

.status-filter {
  display: flex;
  gap: 8px;
  padding: 12px;
  background: white;
  margin-bottom: 12px;
  overflow-x: auto;
  border-bottom: 1px solid #eee;
}

.filter-item {
  padding: 6px 16px;
  border-radius: 16px;
  font-size: 13px;
  background: #f5f5f5;
  color: #666;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s;
}

.filter-item.active {
  background: #1890ff;
  color: white;
}

.order-list {
  padding: 12px;
}

.loading-state,
.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #999;

  .loading-icon,
  .empty-icon {
    font-size: 64px;
    margin-bottom: 16px;
  }
}

.order-card {
  background: white;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  cursor: pointer;
  transition: all 0.2s ease;
}

.order-card:hover {
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  transform: translateY(-2px);
}

.order-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
}

.order-no {
  flex: 1;
  font-size: 14px;
  color: #666;

  .label {
    color: #999;
  }

  .value {
    color: #333;
    font-weight: 500;
  }
}

.order-status {
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  margin-left: 8px;

  &.status-pending {
    background: #fff3e0;
    color: #ff9800;
  }

  &.status-confirmed {
    background: #e3f2fd;
    color: #2196f3;
  }

  &.status-packing {
    background: #fff3e0;
    color: #ff9800;
  }

  &.status-ready {
    background: #e8f5e9;
    color: #4caf50;
  }

  &.status-dispatched {
    background: #e3f2fd;
    color: #2196f3;
  }

  &.status-delivered {
    background: #e8f5e9;
    color: #4caf50;
  }

  &.status-completed {
    background: #e8f5e9;
    color: #4caf50;
  }

  &.status-cancelled {
    background: #ffebee;
    color: #f44336;
  }
}

.order-body {
  margin-bottom: 16px;
}

.info-row {
  display: flex;
  margin-bottom: 8px;
  font-size: 14px;
  line-height: 1.5;

  .label {
    color: #999;
    min-width: 70px;
    flex-shrink: 0;
  }

  .value {
    color: #333;
    flex: 1;

    &.amount {
      font-weight: 600;
      color: #ff6b35;
    }
  }
}

.pending-amount {
  font-size: 12px;
  color: #f59e0b;
  font-weight: 600;
  margin-left: 8px;
}

.items-list {
  flex: 1;
}

.item-row {
  font-size: 13px;
  color: #666;
  margin-bottom: 4px;
}

.order-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.btn-link {
  background: none;
  border: none;
  color: #667eea;
  cursor: pointer;
  font-size: 12px;
  padding: 0;
}
.btn-link:hover { text-decoration: underline; }

// 众包配置样式
.contractor-config {
  .section-title {
    margin-bottom: 16px;
    font-size: 15px;
    font-weight: 600;
    color: #333;
  }

  .form-tip {
    font-size: 12px;
    color: #999;
    margin-top: 4px;
    line-height: 1.4;
  }
}

// 任务类型选择
.task-type-selection {
  margin-bottom: 16px;

  .section-title {
    margin-bottom: 12px;
    font-size: 15px;
    font-weight: 600;
    color: #333;
  }

  .el-radio-group {
    width: 100%;
    display: flex;
    gap: 10px !important;

    .el-radio-button {
      flex: 1;

      .el-radio-button__inner {
        width: 100%;
        text-align: center;
      }
    }
  }
}

.btn {
  flex: 1;
  min-width: 80px;
  padding: 10px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.2s;

  &:active {
    opacity: 0.8;
  }

  &.btn-primary {
    background: #f0f0f0;
    color: #333;
  }

  &.btn-success {
    background: #4caf50;
    color: white;
  }

  &.btn-danger {
    background: #f44336;
    color: white;
  }

  &.btn-warning {
    background: #ff9800;
    color: white;
  }

  &.btn-whatsapp {
    background: #25d366;
    color: white;
  }

  &.btn-share {
    background: #2196f3;
    color: white;
  }

  &.btn-bluetooth {
    background: #667eea;
    color: white;
  }

  &.btn-cloud-print {
    background: #10b981;
    color: white;
  }
}

.paid-badge {
  margin-left: 12px;
  color: #10b981;
  font-size: 16px;
  font-weight: 600;
}

// 创建配送任务对话框样式
.warehouse-selection, .driver-selection {
  margin-bottom: 16px;

  .section-title {
    font-size: 15px;
    font-weight: 600;
    color: #2c3e50;
    margin-bottom: 12px;
  }
}

.warehouse-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 250px;
  overflow-y: auto;

  .warehouse-card {
    padding: 16px;
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      border-color: #667eea;
      background: #f9f9ff;
    }

    &.selected {
      border-color: #667eea;
      background: linear-gradient(135deg, rgba(102,126,234,0.08) 0%, rgba(118,75,162,0.08) 100%);
    }

    &.insufficient {
      border-color: #f0d0d0;
      background: #fff8f8;
      cursor: not-allowed;
      opacity: 0.6;
    }

    .warehouse-header {
      display: flex;
      align-items: flex-start;
      gap: 12px;

      .warehouse-info {
        flex: 1;

        .warehouse-name {
          font-size: 15px;
          font-weight: 600;
          color: #2c3e50;
          margin-bottom: 6px;
          display: flex;
          align-items: center;
        }

        .warehouse-address {
          font-size: 13px;
          color: #666;
          margin-bottom: 8px;
        }

        .product-stocks {
          margin-top: 8px;
          padding-top: 8px;
          border-top: 1px dashed #e0e0e0;

          .stock-item {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 4px 0;
            font-size: 12px;

            .product-name {
              color: #666;
              flex: 1;
            }

            .stock-qty {
              color: #999;
              margin: 0 8px;
            }
          }
        }
      }
    }
  }
}

.driver-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 300px;
  overflow-y: auto;

  .driver-card {
    padding: 16px;
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      border-color: #667eea;
      background: #f9f9ff;
    }

    &.selected {
      border-color: #667eea;
      background: linear-gradient(135deg, rgba(102,126,234,0.08) 0%, rgba(118,75,162,0.08) 100%);
    }

    .driver-header {
      display: flex;
      align-items: flex-start;
      gap: 12px;

      .driver-info {
        flex: 1;

        .driver-name {
          font-size: 15px;
          font-weight: 600;
          color: #2c3e50;
          margin-bottom: 4px;
        }

        .driver-phone {
          font-size: 13px;
          color: #666;
          margin-bottom: 4px;
        }

        .driver-vehicle {
          font-size: 12px;
          color: #999;
        }
      }
    }
  }
}

/* 移动端优化 */
@media (max-width: 768px) {
  .order-header {
    flex-wrap: wrap;
  }

  .order-no {
    width: 100%;
    margin-bottom: 8px;
  }

  .order-actions {
    .btn {
      min-width: calc(50% - 4px);
    }
  }
}

/* 申请售后弹窗 */
.apply-info {
  background: #f0f7ff;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  color: #457b9d;
  margin-bottom: 16px;
}
.form-section { margin-bottom: 16px; }
.form-label { font-size: 13px; font-weight: 600; margin-bottom: 8px; }
.reason-tags { display: flex; flex-wrap: wrap; gap: 6px; }
.reason-tag {
  padding: 6px 10px;
  border: 1px solid #e8e8e8;
  border-radius: 16px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
}
.reason-tag.active { border-color: #1890ff; background: #e6f7ff; color: #1890ff; }

/* 批量选择 */
.batch-action-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  background: #ecf5ff;
  border-bottom: 1px solid #b3d8ff;
}
.batch-info {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #409eff;
}
.order-card {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}
.order-checkbox {
  padding-top: 16px;
  flex-shrink: 0;
}
.order-content {
  flex: 1;
  min-width: 0;
  cursor: pointer;
}
.batch-summary {
  padding: 12px 16px;
  background: #f5f7fa;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 14px;
  color: #606266;
}
</style>
